import React, { useEffect, useState } from 'react';
import { statusReports, nps, projects as projectsApi } from '@/utils/api';
import { BarChart3, FileText, Sparkles, Trash2, Plus, Send, Star, MessageSquare, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function ReportsNps() {
  const [tab, setTab] = useState('reports');
  const [projects, setProjects] = useState([]);
  const [reports, setReports] = useState([]);
  const [npsData, setNpsData] = useState({ items: [], stats: { total: 0, nps: null, promoters: 0, detractors: 0 } });
  const [genFor, setGenFor] = useState('');
  const [busy, setBusy] = useState(false);
  const [inviteForm, setInviteForm] = useState({ project_id: '', email: '' });

  useEffect(() => {
    projectsApi.list().then(r => setProjects(r.data)).catch(() => {});
    reload();
  }, []);

  const reload = () => {
    statusReports.list().then(r => setReports(r.data)).catch(() => {});
    nps.list().then(r => setNpsData(r.data)).catch(() => {});
  };

  const generate = async () => {
    if (!genFor) { toast.error('Pick a project'); return; }
    setBusy(true);
    try {
      await statusReports.generate(genFor, 7);
      toast.success('Report generated');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    if (!inviteForm.project_id || !inviteForm.email) { toast.error('Need project + email'); return; }
    try {
      const r = await nps.invite(inviteForm.project_id, inviteForm.email);
      const link = `${window.location.origin}/nps/${r.data.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Invite created — survey link copied to clipboard');
      setInviteForm({ project_id: '', email: '' });
      reload();
    } catch { toast.error('Failed'); }
  };

  // Compute promoter/passive/detractor distribution from answered items
  const answered = npsData.items.filter(i => i.status === 'answered' && typeof i.score === 'number');
  const total = answered.length || 1;
  const promoters = answered.filter(i => i.score >= 9).length;
  const passives = answered.filter(i => i.score >= 7 && i.score <= 8).length;
  const detractors = answered.filter(i => i.score <= 6).length;
  const recentComments = answered.filter(i => i.comment && i.comment.trim()).slice(0, 5);

  return (
    <div className="p-6 animate-fade-in" data-testid="reports-nps-page">
      <h1 className="text-xl font-black mb-4" style={{ color: 'var(--ink)' }}>Reports & NPS</h1>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('reports')} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1.5" style={{ background: tab === 'reports' ? '#006080' : 'var(--bg)', color: tab === 'reports' ? 'white' : 'var(--muted)', border: '1px solid var(--border)' }} data-testid="tab-reports">
          <FileText size={12} /> Status Reports
        </button>
        <button onClick={() => setTab('nps')} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1.5" style={{ background: tab === 'nps' ? '#006080' : 'var(--bg)', color: tab === 'nps' ? 'white' : 'var(--muted)', border: '1px solid var(--border)' }} data-testid="tab-nps">
          <BarChart3 size={12} /> NPS
        </button>
      </div>

      {tab === 'reports' && (
        <>
          <div className="card-base p-4 mb-5">
            <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}><Sparkles size={13} /> AI-draft a weekly report</h3>
            <div className="flex gap-2">
              <select className="input-agency text-sm flex-1" value={genFor} onChange={e => setGenFor(e.target.value)} data-testid="report-project-select">
                <option value="">Pick a project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button onClick={generate} disabled={busy || !genFor} className="btn-primary text-sm px-4" data-testid="generate-report">
                {busy ? 'Drafting…' : 'Generate'}
              </button>
            </div>
          </div>

          {reports.length === 0 ? (
            <div className="card-base p-8 text-center">
              <FileText size={28} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>No reports yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reports.map(r => {
                const proj = projects.find(p => p.id === r.project_id);
                return (
                  <details key={r.id} className="card-base p-4" data-testid={`report-${r.id}`}>
                    <summary className="cursor-pointer flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{proj?.name || 'Project'}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{new Date(r.generated_at).toLocaleString()}</p>
                      </div>
                      <button onClick={(e) => { e.preventDefault(); statusReports.delete(r.id).then(reload); }} style={{ color: '#B34A3A' }} data-testid={`report-del-${r.id}`}><Trash2 size={12} /></button>
                    </summary>
                    <div className="mt-3 p-3 rounded-btn text-sm whitespace-pre-wrap" style={{ background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'Georgia, serif' }}>
                      {r.markdown}
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'nps' && (
        <>
          {/* Hero stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="card-base p-4">
              <p className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>NPS Score</p>
              <p className="text-3xl font-black" style={{ color: npsData.stats.nps === null ? 'var(--muted)' : npsData.stats.nps >= 30 ? '#3C8A5A' : npsData.stats.nps >= 0 ? '#D9A441' : '#B34A3A' }}>
                {npsData.stats.nps ?? '—'}
              </p>
              <p className="text-[10px] mt-0.5 uppercase tracking-wide font-semibold" style={{ color: 'var(--muted)' }}>-100 … +100</p>
            </div>
            <div className="card-base p-4">
              <p className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--muted)' }}>Responses</p>
              <p className="text-3xl font-black" style={{ color: 'var(--ink)' }}>{npsData.stats.total}</p>
            </div>
            <div className="card-base p-4">
              <p className="text-xs font-bold uppercase mb-1" style={{ color: '#3C8A5A' }}>Promoters</p>
              <p className="text-3xl font-black" style={{ color: '#3C8A5A' }}>{promoters}</p>
            </div>
            <div className="card-base p-4">
              <p className="text-xs font-bold uppercase mb-1" style={{ color: '#B34A3A' }}>Detractors</p>
              <p className="text-3xl font-black" style={{ color: '#B34A3A' }}>{detractors}</p>
            </div>
          </div>

          {/* Distribution bar */}
          {answered.length > 0 && (
            <div className="card-base p-4 mb-5" data-testid="nps-distribution">
              <p className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--muted)' }}>Distribution</p>
              <div className="flex h-6 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                <div style={{ background: '#3C8A5A', width: `${(promoters/total)*100}%` }} title={`${promoters} promoters (9–10)`} />
                <div style={{ background: '#D9A441', width: `${(passives/total)*100}%` }} title={`${passives} passives (7–8)`} />
                <div style={{ background: '#B34A3A', width: `${(detractors/total)*100}%` }} title={`${detractors} detractors (0–6)`} />
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--muted)' }}>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#3C8A5A' }} /> Promoters 9–10 · {promoters}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#D9A441' }} /> Passives 7–8 · {passives}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#B34A3A' }} /> Detractors 0–6 · {detractors}</span>
              </div>
            </div>
          )}

          {/* Recent comments */}
          {recentComments.length > 0 && (
            <div className="card-base p-4 mb-5" data-testid="nps-recent-comments">
              <p className="text-xs font-bold uppercase mb-3 flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>
                <MessageSquare size={11} /> Recent comments
              </p>
              <div className="space-y-2">
                {recentComments.map(c => (
                  <div key={c.id} className="flex gap-3 items-start">
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{
                      background: c.score >= 9 ? '#3C8A5A15' : c.score >= 7 ? '#D9A44115' : '#B34A3A15',
                      color: c.score >= 9 ? '#3C8A5A' : c.score >= 7 ? '#D9A441' : '#B34A3A',
                    }}>
                      {c.score}
                    </span>
                    <p className="text-sm flex-1 italic" style={{ color: 'var(--ink)' }}>"{c.comment}"</p>
                    <span className="text-xs flex-shrink-0" style={{ color: 'var(--muted)' }}>{c.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card-base p-4 mb-5">
            <h3 className="font-bold text-sm mb-2 flex items-center gap-1.5" style={{ color: 'var(--ink)' }}><Plus size={13} /> Invite to survey</h3>
            <div className="grid grid-cols-3 gap-2">
              <select className="input-agency text-sm" value={inviteForm.project_id} onChange={e => setInviteForm({...inviteForm, project_id: e.target.value})} data-testid="nps-project">
                <option value="">Pick project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input className="input-agency text-sm" type="email" placeholder="Client email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} data-testid="nps-email" />
              <button className="btn-primary text-sm flex items-center gap-1.5 justify-center" onClick={invite} data-testid="nps-invite-btn"><Send size={12} /> Create invite</button>
            </div>
          </div>

          <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Invites</h3>
          {npsData.items.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Star size={28} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>No invites yet</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {npsData.items.map(i => (
                <div key={i.id} className="card-base p-3 flex items-center justify-between" data-testid={`nps-invite-${i.id}`}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{i.email}</p>
                    <p className="text-xs font-mono break-all" style={{ color: 'var(--muted)' }}>/nps/{i.token.slice(0, 20)}…</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {i.status === 'answered' ? (
                      <span className="badge-status" style={{
                        background: i.score >= 9 ? '#3C8A5A15' : i.score >= 7 ? '#D9A44115' : '#B34A3A15',
                        color: i.score >= 9 ? '#3C8A5A' : i.score >= 7 ? '#D9A441' : '#B34A3A',
                      }}>
                        Score: {i.score}
                      </span>
                    ) : (
                      <span className="badge-status" style={{ background: '#D9A44115', color: '#D9A441' }}>Pending</span>
                    )}
                    <button
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/nps/${i.token}`); toast.success('Link copied'); }}
                      className="text-xs flex items-center gap-1 px-2 py-1 rounded-btn"
                      style={{ color: '#006080' }}
                    >
                      <Copy size={10} /> link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
